import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, FolderPlus, Plus, Search } from 'lucide-react';
import type { CampaignListItem } from '../../shared/types';
import { api } from '../lib/api';
import { formatDate, relativeTime } from '../lib/format';
import { isAgencyUser, useMe } from '../lib/session';
import { useTitle } from '../components/AppShell';
import { CampaignDialog } from '../components/CampaignDialog';
import { EmptyState, Notice, ProgressBar, Spinner } from '../components/ui';
import { errorMessage } from '../components/toast';

const onboardingKey = (userId: string) => `zeigbanner.onboarding.${userId}`;

function readDismissed(userId: string): boolean {
  try {
    return localStorage.getItem(onboardingKey(userId)) === 'done';
  } catch {
    return false;
  }
}

/** Kurze Einführung für neue Agentur-Konten – bleibt sichtbar, bis man sie ausblendet. */
function Onboarding({
  firstCampaignId,
  isAdmin,
  onCreate,
}: {
  firstCampaignId?: string;
  isAdmin: boolean;
  onCreate: () => void;
}) {
  const { data: me } = useMe();
  const [hidden, setHidden] = useState(() => (me ? readDismissed(me.id) : true));
  if (!me || hidden) return null;
  const firstName = (me.name || '').split(' ')[0];
  const steps: { title: string; text: string; action?: ReactNode }[] = [
    {
      title: 'Beispiel ansehen',
      text: 'Eine fertige Kampagne zeigt Vorschau, Technik-Check, Kommentare und Freigaben.',
      action: firstCampaignId ? (
        <Link className="link-arrow" to={`/kampagnen/${firstCampaignId}`}>
          Öffnen
        </Link>
      ) : undefined,
    },
    {
      title: 'Kunden anlegen',
      text: 'Mit Logo – es erscheint später auf den Freigabe-Links.',
      action: (
        <Link className="link-arrow" to="/kunden">
          Kunden
        </Link>
      ),
    },
    {
      title: 'Kampagne anlegen',
      text: 'Dann die Banner einfach in die Kampagne ziehen – ZIP, GIF, Bild oder Video.',
      action: (
        <button className="link-arrow" onClick={onCreate}>
          Neue Kampagne
        </button>
      ),
    },
    {
      title: 'Teilen',
      text: 'In der Kampagne auf „Teilen“: Kund:innen kommentieren und geben frei, ohne Konto.',
    },
  ];
  if (isAdmin) {
    steps.push({
      title: 'Team und Aussehen',
      text: 'Kolleg:innen einladen, Logo und Farben unter Einstellungen prüfen.',
      action: (
        <Link className="link-arrow" to="/einstellungen">
          Einstellungen
        </Link>
      ),
    });
  }
  return (
    <section className="onboarding" aria-label="Erste Schritte">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="grow">
          <span className="eyebrow">Erste Schritte</span>
          <h2 className="section-title" style={{ marginTop: 6 }}>
            {firstName ? `Willkommen, ${firstName}!` : 'Willkommen!'} So startest du.
          </h2>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            try {
              localStorage.setItem(onboardingKey(me.id), 'done');
            } catch {
              // privater Modus – dann nur für diese Sitzung ausgeblendet
            }
            setHidden(true);
          }}
        >
          Ausblenden
        </button>
      </div>
      <ol className="onboarding-steps">
        {steps.map((step) => (
          <li key={step.title}>
            <strong>{step.title}</strong>
            <span className="small muted">{step.text}</span>
            {step.action}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function CampaignsPage() {
  useTitle('Kampagnen');
  const { data: me } = useMe();
  const agency = isAgencyUser(me);
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const campaigns = useQuery({
    queryKey: ['campaigns', status],
    queryFn: () => api<CampaignListItem[]>(`/api/campaigns?status=${status}`),
  });

  const clients = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of campaigns.data ?? []) map.set(c.client.id, c.client.name);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [campaigns.data]);

  const list = (campaigns.data ?? []).filter((c) => {
    if (clientFilter && c.client.id !== clientFilter) return false;
    const q = query.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.client.name.toLowerCase().includes(q);
  });

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-text">
          <span className="eyebrow">{agency ? 'Übersicht' : me?.clientName}</span>
          <h1 className="page-title">Kampagnen</h1>
        </div>
        {agency && (
          <div className="page-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={18} /> Neue Kampagne
            </button>
          </div>
        )}
      </div>

      {agency && status === 'active' && campaigns.data && (
        <Onboarding
          firstCampaignId={campaigns.data[0]?.id}
          isAdmin={me?.role === 'admin'}
          onCreate={() => setCreating(true)}
        />
      )}

      <div className="filters">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--muted)' }} />
          <input
            className="input"
            style={{ paddingLeft: 34, width: 260 }}
            placeholder="Suchen …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Kampagnen durchsuchen"
          />
        </div>
        {agency && clients.length > 1 && (
          <select
            className="select"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            aria-label="Kunde filtern"
          >
            <option value="">Alle Kunden</option>
            {clients.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        )}
        <div className="spacer" />
        <div className="segmented" role="group" aria-label="Status">
          <button aria-pressed={status === 'active'} onClick={() => setStatus('active')}>
            Aktiv
          </button>
          <button aria-pressed={status === 'archived'} onClick={() => setStatus('archived')}>
            Archiv
          </button>
        </div>
      </div>

      {campaigns.isLoading && <Spinner />}
      {campaigns.error && <Notice kind="error">{errorMessage(campaigns.error)}</Notice>}
      {campaigns.data && list.length === 0 && (
        <EmptyState
          icon={<FolderPlus size={34} />}
          title={status === 'archived' ? 'Archiv ist leer' : 'Noch keine Kampagnen'}
        >
          {agency && status === 'active' ? (
            <>
              <p>Lege eine Kampagne an und zieh die Werbemittel hinein.</p>
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <Plus size={18} /> Neue Kampagne
              </button>
            </>
          ) : (
            <p>Sobald die Agentur etwas zur Abstimmung bereitstellt, erscheint es hier.</p>
          )}
        </EmptyState>
      )}

      {list.length > 0 && (
        <div className="campaign-list">
          {list.map((c) => (
            <Link key={c.id} to={`/kampagnen/${c.id}`} className="campaign-row">
              <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                <span className="eyebrow">{c.client.name}</span>
                <h3>{c.name}</h3>
                <div className="row-wrap small muted">
                  {agency && !c.visibleToClient && <span className="chip chip-outline">Intern</span>}
                  {c.dueDate && <span>Freigabe bis {formatDate(c.dueDate)}</span>}
                </div>
              </div>
              <div>
                <div className="stat-line">
                  <span>
                    <strong>{c.stats.approved}</strong> von {c.stats.total} freigegeben
                  </span>
                  {c.stats.changesRequested > 0 && (
                    <span className="status-fail">{c.stats.changesRequested} Änderungen</span>
                  )}
                </div>
                <ProgressBar
                  total={c.stats.total}
                  approved={c.stats.approved}
                  changes={c.stats.changesRequested}
                />
              </div>
              <div className="small">
                {c.stats.openComments > 0 ? (
                  <span>
                    <strong>{c.stats.openComments}</strong> offene Kommentare
                  </span>
                ) : (
                  <span className="muted">Keine offenen Kommentare</span>
                )}
              </div>
              <div className="small muted">{relativeTime(c.lastActivityAt ?? c.createdAt)}</div>
              <ChevronRight className="chev" size={20} />
            </Link>
          ))}
        </div>
      )}
      <CampaignDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
